/**
 * Peças reutilizáveis da interface.
 *
 * Ficam num arquivo só de propósito: são componentes pequenos, sem estado, que
 * quase sempre são usados juntos. Um arquivo por peça aqui geraria dez arquivos
 * de vinte linhas e dez imports em cada página, sem ganho nenhum.
 */

import type { CSSProperties, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { cn } from '@/lib/cn';
import { PLATFORM_LABEL, STATUS_LABEL, TOURNAMENT_STATUS_LABEL } from '@/lib/format';
import type { TeamDto } from '@/lib/api';
import { ehEscudoPadrao, lerEscudo } from '@mpc/core';
import { EscudoGerado } from './EscudoGerado';

/* ------------------------------------------------------------------ *
 * Identidade do time
 * ------------------------------------------------------------------ */

const CREST_SIZE = {
  sm: 'size-6 text-[0.5rem]',
  md: 'size-9 text-[0.6875rem]',
  lg: 'size-14 text-base',
  xl: 'size-20 text-xl',
} as const;

/**
 * Escudo do time. Sem imagem enviada, cai no escudo gerado do nome (formato,
 * padrão e cores de clube), estável para o mesmo time em qualquer tela, em vez
 * de um quadrado com duas letras.
 */
export function TeamCrest({
  team,
  size = 'md',
  className,
}: {
  team: Pick<TeamDto, 'name' | 'shortName' | 'crestUrl'> | null | undefined;
  size?: keyof typeof CREST_SIZE;
  className?: string;
}) {
  if (!team) {
    return (
      <span
        className={cn(
          CREST_SIZE[size],
          'inline-flex shrink-0 items-center justify-center border border-dashed border-line-strong text-faint',
          className,
        )}
        aria-hidden
      >
        ?
      </span>
    );
  }

  if (team.crestUrl && !ehEscudoPadrao(team.crestUrl)) {
    return (
      <img
        src={team.crestUrl}
        alt=""
        loading="lazy"
        className={cn(CREST_SIZE[size], 'shrink-0 object-contain', className)}
      />
    );
  }

  // Sem imagem enviada, vale o escudo padrão gravado no time (o que o capitão
  // escolheu) e, na falta dele, o que o nome sorteia.
  return (
    <EscudoGerado
      nome={team.name}
      sigla={team.shortName}
      escudo={lerEscudo(team.crestUrl)}
      className={cn(CREST_SIZE[size], 'shrink-0 drop-shadow-[0_2px_3px_rgb(0_0_0/0.35)]', className)}
    />
  );
}

/** Nome + escudo, o par que aparece em toda tabela e linha de partida. */
export function TeamLabel({
  team,
  size = 'md',
  reverse = false,
  short = false,
  className,
}: {
  team: TeamDto | null | undefined;
  size?: keyof typeof CREST_SIZE;
  reverse?: boolean;
  short?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex min-w-0 items-center gap-2.5',
        reverse && 'flex-row-reverse text-right',
        className,
      )}
    >
      <TeamCrest team={team} size={size} />
      <span className="truncate font-medium">
        {team ? (short ? team.shortName : team.name) : 'A definir'}
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Badges
 * ------------------------------------------------------------------ */

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'brand' | 'win' | 'loss' | 'live' | 'warn';
  className?: string;
}) {
  const tones = {
    neutral: 'border-line-strong bg-surface-2 text-muted',
    brand: 'border-brand/30 bg-brand/10 text-brand-ink',
    win: 'border-win/30 bg-win/10 text-win',
    loss: 'border-loss/30 bg-loss/10 text-loss',
    live: 'border-live/40 bg-live/10 text-live',
    warn: 'border-draw/40 bg-draw/10 text-draw',
  } as const;

  return (
    <span
      className={cn(
        // Pílula na caixa normal, como as etiquetas do template — o selo em caixa
        // alta espaçada era da voz antiga do produto.
        'inline-flex items-center gap-1.5 border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function PlatformBadge({ platform }: { platform: keyof typeof PLATFORM_LABEL }) {
  return <Badge>{PLATFORM_LABEL[platform] ?? platform}</Badge>;
}

export function MatchStatusBadge({ status }: { status: keyof typeof STATUS_LABEL }) {
  const tone =
    status === 'live'
      ? 'live'
      : status === 'finished'
        ? 'win'
        : status === 'walkover'
          ? 'loss'
          : status === 'disputed'
            ? 'warn'
            : 'neutral';

  return (
    <Badge tone={tone}>
      {status === 'live' && (
        <span className="size-1.5 animate-pulse bg-live" aria-hidden />
      )}
      {STATUS_LABEL[status] ?? status}
    </Badge>
  );
}

export function TournamentStatusBadge({
  status,
}: {
  status: keyof typeof TOURNAMENT_STATUS_LABEL;
}) {
  // Vermelho (`live`) fica reservado para partida acontecendo agora. Um
  // campeonato em fase de grupos está saudável, não em alerta.
  const tone =
    status === 'registration'
      ? 'brand'
      : status === 'group_stage' || status === 'knockout'
        ? 'win'
        : 'neutral';
  return <Badge tone={tone}>{TOURNAMENT_STATUS_LABEL[status] ?? status}</Badge>;
}

/* ------------------------------------------------------------------ *
 * Estrutura de página
 * ------------------------------------------------------------------ */

/**
 * O casco de toda página interna.
 *
 * O fundo é só a cor escura do site. Ele já levou um brilho oliva descendo
 * do topo, a mesma luz da hero da home; saiu porque o verde por trás de cada
 * página competia com o que ela mostra — no perfil de um time, misturava a
 * cor da Hexa Cup com as cores do clube. A luz verde fica sendo da home.
 *
 * O `pt-36` é a folga da navbar em pílula, que flutua a 1.5rem do topo — a
 * mesma reserva de 9rem que a hero da home faz.
 */
export function PageShell({ children, className }: { children: ReactNode; className?: string }) {
  /*
   * A largura padrão só entra quando a página não pediu a dela.
   *
   * O `cn` junta as classes com o tailwind-merge, que desempata as que brigam
   * entre si — mas ele não reconhece `max-w-mpc` como largura (o nome é nosso,
   * não do Tailwind). Com isso `max-w-mpc` e o `max-w-2xl` de uma página de
   * formulário sobreviviam os dois, e quem decidia era a ordem no CSS: os
   * 110rem venciam. O resultado eram campos de formulário esticados por 1700px
   * numa tela larga — em catorze páginas, todas as que passam uma largura.
   */
  const temLarguraPropria = /(^|\s)max-w-/.test(className ?? '');

  return (
    <div className="relative isolate">
      <div
        className={cn(
          'mx-auto w-full px-6 pt-36 pb-20',
          !temLarguraPropria && 'max-w-mpc',
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  marca,
  voltar = true,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Imagem que identifica a pagina — escudo do time, brasao da edicao. */
  marca?: ReactNode;
  /**
   * Seta de voltar. Ligada por padrão: quem entra numa página de detalhe —
   * um campeonato, um time, uma partida — chegou de alguma lista, e voltar
   * para ela é o gesto mais repetido do sistema. Desligue nas páginas que são
   * o começo da navegação (home, painel), onde a seta só teria o histórico do
   * navegador para oferecer.
   */
  voltar?: boolean;
}) {
  return (
    /*
      O cabeçalho das páginas internas segue o bloco de título das páginas do
      template (`.fixtures-hero-block`): etiqueta em pílula, título Anton na
      escala do h2 (3.125rem, entrelinha 1, -0.0625rem de espaçamento) e o
      parágrafo grande de 1.125rem. Fica alinhado à esquerda, e não centrado
      como no original, porque aqui ele divide a linha com a seta de voltar, o
      escudo do time e as ações da página — centrado, o título ficaria órfão
      entre elas. Sem filete embaixo: o template separa por espaço, não por linha.
    */
    <header className="mb-12 flex flex-wrap items-end justify-between gap-6">
      <div className="flex min-w-0 items-center gap-5">
        {voltar && <BotaoVoltar />}
        {marca}
        <div className="min-w-0">
          {eyebrow && <div className="mpc-etiqueta mb-5">{eyebrow}</div>}
          <h1 className="mpc-display text-[clamp(2.25rem,5vw,3.125rem)] leading-none tracking-[-0.0625rem]">
            {title}
          </h1>
          {description && <p className="mt-4 max-w-2xl text-lg leading-[1.556] text-muted">{description}</p>}
        </div>
      </div>
      {/*
          `shrink-0` sem `flex-wrap` era a causa do transbordo horizontal no
          celular: com dois ou tres botoes o bloco chegava a 479px e empurrava a
          pagina inteira para 504px de largura numa tela de 390. Agora ele
          quebra linha, e o `shrink-0` sai — encolher e exatamente o que se
          espera dele quando nao ha espaco.
      */}
      {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
    </header>
  );
}

/**
 * Volta uma entrada no histórico.
 *
 * Usa o histórico, e não uma rota fixa, porque a mesma página é alcançada de
 * vários lugares: um time vem da lista de times, da classificação de um grupo
 * ou do card de uma partida, e mandar sempre para `/times` levaria o visitante
 * a um lugar em que ele não estava.
 *
 * Quando não há para onde voltar — link colado, aba nova, primeira página da
 * sessão — a seta some em vez de virar um botão que não faz nada.
 */
function BotaoVoltar() {
  const navegar = useNavigate();
  const podeVoltar = typeof window !== 'undefined' && window.history.length > 1;

  if (!podeVoltar) return null;

  return (
    <button
      type="button"
      onClick={() => navegar(-1)}
      aria-label="Voltar para a página anterior"
      title="Voltar"
      className="grid size-12 shrink-0 place-items-center bg-(--vidro) text-paper transition-colors hover:bg-brand hover:text-black"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-5"
        aria-hidden
      >
        <path d="M19 12H5" />
        <path d="m12 19-7-7 7-7" />
      </svg>
    </button>
  );
}

export function Panel({
  title,
  aside,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn('mpc-panel overflow-hidden', className)}>
      {(title || aside) && (
        <header className="flex items-center justify-between gap-4 border-b border-line px-6 py-4">
          {title && <h2 className="mpc-display text-xl">{title}</h2>}
          {aside}
        </header>
      )}
      <div className={cn(bodyClassName)}>{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Estados
 * ------------------------------------------------------------------ */

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="px-6 py-14 text-center">
      <p className="mpc-display text-xl text-paper">{title}</p>
      {description && <p className="mx-auto mt-2 max-w-md text-sm text-muted">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse bg-surface-2', className)} aria-hidden />;
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message =
    error instanceof Error ? error.message : 'Não foi possível carregar estes dados.';
  return (
    <div className="px-6 py-14 text-center" role="alert">
      <p className="mpc-display text-xl text-loss">Deu problema aqui</p>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-6 border border-paper px-6 py-3 text-sm font-medium transition-colors hover:bg-paper hover:text-ink"
        >
          Tentar de novo
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Navegação por abas
 * ------------------------------------------------------------------ */

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  className,
}: {
  /** `alerta`: pendências que pedem ação -- aparece em destaque, e some quando é zero. */
  tabs: ReadonlyArray<{ id: T; label: string; count?: number; alerta?: number }>;
  active: T;
  onChange: (id: T) => void;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      /*
        As abas do template (`.tournaments-tab-link`): peças soltas de fundo
        translúcido e raio de 0.75rem, e a ativa ganha contorno claro e perde o
        fundo. Substituem o sublinhado sobre um filete, que era a aba do
        sistema antigo e não existe em lugar nenhum da home.
      */
      className={cn('flex gap-2 overflow-x-auto overflow-y-hidden pb-1 no-scrollbar', className)}
    >
      {tabs.map((tab) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={selected}
            onClick={() => onChange(tab.id)}
            className={cn(
              'relative shrink-0 border px-5 py-3 text-base whitespace-nowrap transition-colors duration-[--duration-quick]',
              selected
                ? 'border-paper bg-transparent text-paper'
                : 'border-transparent bg-(--vidro) text-muted hover:text-paper',
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="tabular ml-2 text-xs text-faint">{tab.count}</span>
            )}
            {tab.alerta !== undefined && tab.alerta > 0 && (
              <span
                className="tabular ml-2 inline-flex min-w-5 items-center justify-center bg-brand px-1.5 py-0.5 text-[0.6875rem] leading-none font-bold text-black"
                aria-label={`${tab.alerta} ${tab.alerta === 1 ? 'pendente' : 'pendentes'}`}
              >
                {tab.alerta}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export * from './CampoDeSenha';
export * from './ConfirmModal';
export * from './DateTimePicker';


